import React from 'react';

/**
 * Multi-column table filtering.
 *
 * The rules, which are the standard spreadsheet ones because they are the ones
 * people already have in their hands:
 *
 *   - Within a column, ticked values are OR'd. Status = Accepted or Rejected.
 *   - Across columns, filters are AND'd. Status = Accepted AND Track = Methods.
 *   - An empty selection means "no filter on this column", not "hide everything".
 *     This is the distinction that matters most: unticking the last box has to
 *     mean you stopped filtering, or the table goes blank and looks broken.
 *
 * Option counts for a column are computed with that column's OWN selection
 * lifted and every other column's still applied — so the number beside a value
 * tells you what ticking it would ADD, and a 0 is visibly a dead end before you
 * spend a click on it.
 *
 * Usage:
 *
 *   const columns = {
 *     status: { label: 'Status', accessor: (r) => r.status, format: (v) => label(v) },
 *     track:  { label: 'Track',  accessor: (r) => r.track },
 *   };
 *   const f = useTableFilters(rows, columns);
 *   const visible = f.filterRows(rows);
 *
 *   <SortableTh field="status" sort={sort} onSort={toggle} filter={f.headerProps('status')}>
 *
 * `rows` should be the set BEFORE filtering — the hook needs the full population
 * to count against. Pass the same array you pass to `filterRows`.
 */
export default function useTableFilters(rows, columns) {
  // { [field]: string[] } — only fields with a non-empty selection appear.
  const [selections, setSelections] = React.useState({});

  const cols = React.useMemo(() => columns || {}, [columns]);

  /** A row's value for a column, normalised to a string key. */
  const valueOf = React.useCallback((row, field) => {
    const c = cols[field];
    if (!c) return undefined;
    const raw = typeof c.accessor === 'function' ? c.accessor(row) : row?.[c.accessor ?? field];
    if (raw === null || raw === undefined || raw === '') return '';
    return String(raw);
  }, [cols]);

  /** Does this row pass every column filter except the ones named in `skip`? */
  const passes = React.useCallback((row, skip) => {
    for (const field of Object.keys(selections)) {
      if (field === skip) continue;
      const sel = selections[field];
      if (!sel || sel.length === 0) continue;
      if (!sel.includes(valueOf(row, field))) return false;
    }
    return true;
  }, [selections, valueOf]);

  const filterRows = React.useCallback(
    (list) => (Array.isArray(list) ? list.filter((r) => passes(r, null)) : list),
    [passes]
  );

  /**
   * Distinct values for a column, with counts, ordered by the column's own
   * `order` if given, otherwise by count descending then label.
   */
  const optionsFor = React.useCallback((field) => {
    const c = cols[field];
    if (!c || !Array.isArray(rows)) return [];
    const counts = new Map();
    for (const row of rows) {
      if (!passes(row, field)) continue;       // own filter lifted, others applied
      const v = valueOf(row, field);
      counts.set(v, (counts.get(v) || 0) + 1);
    }
    // Values that exist in the population but are currently counted out still
    // need to appear, or a ticked value could vanish from its own menu.
    for (const row of rows) {
      const v = valueOf(row, field);
      if (!counts.has(v)) counts.set(v, 0);
    }

    const fmt = c.format || ((v) => (v === '' ? (c.emptyLabel || '—') : v));
    const out = [...counts.entries()].map(([value, count]) => ({
      value,
      count,
      label: String(fmt(value) ?? value),
    }));

    if (Array.isArray(c.order)) {
      const idx = (v) => { const i = c.order.indexOf(v); return i === -1 ? 999 : i; };
      out.sort((a, b) => idx(a.value) - idx(b.value) || a.label.localeCompare(b.label));
    } else {
      out.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { numeric: true }));
    }
    return out;
  }, [cols, rows, passes, valueOf]);

  const toggle = React.useCallback((field, value) => {
    setSelections((prev) => {
      const all = new Set();
      // Toggling the first box has to start from "everything ticked", so the
      // first click means "just this one off", matching what the boxes show.
      const current = prev[field];
      if (!current || current.length === 0) {
        for (const row of rows || []) all.add(valueOf(row, field));
        all.delete(value);
        const next = [...all];
        // Unticking the only value would mean an empty selection, which reads
        // as "no filter". Keep it explicit by storing every other value.
        return next.length === 0 ? prev : { ...prev, [field]: next };
      }
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      if (next.length === 0) {
        const { [field]: _drop, ...rest } = prev;   // back to unfiltered
        return rest;
      }
      return { ...prev, [field]: next };
    });
  }, [rows, valueOf]);

  const only = React.useCallback((field, value) => {
    setSelections((prev) => ({ ...prev, [field]: [value] }));
  }, []);

  const clearColumn = React.useCallback((field) => {
    setSelections((prev) => { const { [field]: _d, ...rest } = prev; return rest; });
  }, []);

  const selectAll = React.useCallback((field) => clearColumn(field), [clearColumn]);
  const clearAll = React.useCallback(() => setSelections({}), []);

  const activeFields = Object.keys(selections).filter((f) => selections[f]?.length);
  const activeCount = activeFields.length;

  /** Everything a <SortableTh> needs to render and drive its menu. */
  const headerProps = React.useCallback((field) => {
    const c = cols[field];
    if (!c) return undefined;
    return {
      field,
      label: c.label || field,
      selected: selections[field] || [],
      options: optionsFor(field),
      onToggle: (v) => toggle(field, v),
      onOnly: (v) => only(field, v),
      onClear: () => clearColumn(field),
      onSelectAll: () => selectAll(field),
    };
  }, [cols, selections, optionsFor, toggle, only, clearColumn, selectAll]);

  /**
   * Chips for <ActiveFilters>. One chip per column rather than per value —
   * "Status: Accepted +2" stays readable where six separate chips do not.
   */
  const chips = React.useMemo(() => activeFields.map((field) => {
    const c = cols[field];
    const sel = selections[field];
    const fmt = c?.format || ((v) => (v === '' ? (c?.emptyLabel || '—') : v));
    const head = String(fmt(sel[0]) ?? sel[0]);
    return {
      key: `col:${field}`,
      label: c?.label || field,
      value: sel.length === 1 ? head : `${head} +${sel.length - 1}`,
      onRemove: () => clearColumn(field),
    };
  }), [activeFields, cols, selections, clearColumn]);

  return {
    selections, filterRows, headerProps, chips,
    activeCount, activeFields, clearAll, clearColumn,
  };
}
